// Dictation for the Gable composer, on the Web Speech API. Ported from the
// design system's `use-dictation.ts` hook (ui-kit) — same state machine, no
// React. The whole model:
//
// - The transcript lives IN the input, exactly like native OS dictation:
//   finalized speech is committed to the caller's value, the in-flight
//   (interim) fragment is appended for display only, and the two read as one
//   piece of text. No overlay, no separate transcript panel.
// - The keyboard always wins. A keystroke while listening stops recognition
//   and takes the field's visible text (committed + interim + the keystroke)
//   wholesale as the new value — nothing spoken is lost, and the caret is
//   wherever the person put it.
// - Stopping is never destructive: toggle, Escape, silence timeout and send
//   all keep the words. `capture()` exists because `recognition.stop()`
//   finalizes ASYNCHRONOUSLY — a send handler that stopped and then read the
//   value would lose the interim tail, so it snapshots synchronously and
//   aborts instead. Callers MUST use what `capture()` returns rather than
//   re-reading the draft after calling it.
// - Unsupported browsers (Firefox, some WebViews) report `supported === false`;
//   callers omit the mic entirely — a mic that cannot listen is a broken
//   control, not a disabled one.
//
// The React refs (`valueRef`, `interimRef`, `listeningRef`) are plain instance
// fields here for exactly the reason they were refs there: recognition event
// handlers need a synchronous read of the current state.

import { dictationDiag } from './dictation-diagnostics';
import { punctuateChunk } from './punctuate';

/** What happened, what it did not do, one way forward. */
export const MIC_BLOCKED_MESSAGE =
    'Gable can’t use your microphone — access is blocked for this site, and nothing was recorded. Allow the microphone in your browser’s site settings, then tap the mic again.';

/* The Web Speech API is not in TypeScript's dom lib — minimal local shapes. */
export interface SpeechAlternativeLike {
    transcript: string;
}
export interface SpeechResultLike {
    isFinal: boolean;
    0: SpeechAlternativeLike;
}
export interface SpeechResultEventLike {
    resultIndex: number;
    results: ArrayLike<SpeechResultLike>;
}
export interface SpeechErrorEventLike {
    error: string;
}
export interface SpeechRecognitionLike {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    /**
     * On-device recognition where the browser has it. Chrome streams audio to
     * Google's servers otherwise, and residents' names and financial details
     * get spoken into this box — see `probeLocalProcessing()`.
     */
    processLocally?: boolean;
    onresult: ((event: SpeechResultEventLike) => void) | null;
    onerror: ((event: SpeechErrorEventLike) => void) | null;
    onend: (() => void) | null;
    /*
     * Lifecycle events the controller does not act on. They are wired to the
     * diagnostic trace only, because they are what tells a recognizer that
     * went quiet apart from one that ended: `audioend`/`speechend` with no
     * `end` after them is the browser dropping the session without saying so.
     */
    onstart?: (() => void) | null;
    onaudiostart?: (() => void) | null;
    onaudioend?: (() => void) | null;
    onspeechstart?: (() => void) | null;
    onspeechend?: (() => void) | null;
    onsoundend?: (() => void) | null;
    onnomatch?: (() => void) | null;
    start: () => void;
    abort: () => void;
}

/** Trace-only lifecycle handlers; see the note on `SpeechRecognitionLike`. */
const TRACE_EVENTS = [
    'onstart',
    'onaudiostart',
    'onaudioend',
    'onspeechstart',
    'onspeechend',
    'onsoundend',
    'onnomatch',
] as const;

export interface GableDictationOptions {
    /**
     * Cap on the committed draft, in characters. The textarea's `maxlength`
     * only constrains the keyboard — a programmatic write goes straight past
     * it — so dictation enforces the same limit here.
     */
    maxLength?: number;
}

export type SpeechAvailability = 'available' | 'downloadable' | 'downloading' | 'unavailable';

export interface SpeechRecognitionCtor {
    new (): SpeechRecognitionLike;
    /**
     * Added to the spec after the original API — present in recent Chrome,
     * absent everywhere else, so every read of it is feature-detected.
     */
    available?: (options: { langs: string[]; processLocally: boolean }) => Promise<SpeechAvailability>;
}

/** Everything the controller needs from the component that owns it. */
export interface GableDictationHost {
    /**
     * The committed draft, read lazily on every access — the service clears it
     * on send, so a value captured once goes stale and the next dictation
     * would join onto dead text.
     */
    getValue(): string;
    /** Commit text to the draft (`GableDrawerService.setDraft`). */
    setValue(next: string): void;
    /** Something the template renders changed — mark for check and re-autosize. */
    onChange(): void;
    /**
     * Run a callback inside Angular's zone. Recognition events may or may not
     * be zone-patched depending on the Angular/zone.js version, and wrapping
     * unconditionally is idempotent when they are.
     */
    run(fn: () => void): void;
    /**
     * Whether the caller is already inside Angular's zone. Diagnostic only —
     * it answers whether zone.js patches SpeechRecognition in this version,
     * which `run()` makes moot for correctness either way.
     */
    isInAngularZone(): boolean;
}

/**
 * The browser's recognition constructor, or undefined where there is none
 * (Firefox). Callers resolve it and hand it to the controller rather than the
 * controller defaulting to it: a default parameter is also what an explicit
 * `undefined` argument selects, so a spec could not express "no API here"
 * while running in a Chrome that has one.
 */
export const getRecognitionCtor = (): SpeechRecognitionCtor | undefined => {
    if (typeof window === 'undefined') {
        return undefined;
    }
    const w = window as unknown as Record<string, SpeechRecognitionCtor | undefined>;
    return w['SpeechRecognition'] ?? w['webkitSpeechRecognition'];
};

/** Glue speech onto typed text with exactly one space at the seam. */
const join = (base: string, addition: string): string => {
    if (!addition) {
        return base;
    }
    if (!base) {
        return addition;
    }
    return `${base.replace(/[ \t]+$/, '')}${/\s$/.test(base) ? '' : ' '}${addition}`;
};

export class GableDictationController {
    /** False where the browser has no SpeechRecognition — do not render a mic. */
    readonly supported: boolean;

    listening = false;
    /** The mic-blocked message, or null. Render it as an inline notice. */
    error: string | null = null;

    private _interim = '';
    private _recognition: SpeechRecognitionLike | null = null;
    /**
     * Snapshot at the moment recording starts, so `cancel()` has something to
     * revert to — a session's finals are committed as they arrive, not batched.
     */
    private _preListenValue = '';
    /** Cached `SpeechRecognition.available()` verdict; see `probeLocalProcessing`. */
    private _localProcessing = false;
    /**
     * How many entries of the session's `results` list have already been
     * committed. `resultIndex` is "the lowest index that changed", and Chrome
     * points it back at an already-final result when the next interim fragment
     * opens — walking from it alone re-commits that sentence every time.
     */
    private _consumedFinals = 0;
    /** `probeLocalProcessing()` runs once, lazily — see `start()`. */
    private _localProcessingProbed = false;

    constructor(
        private readonly _host: GableDictationHost,
        private readonly _ctor: SpeechRecognitionCtor | undefined,
        private readonly _options: GableDictationOptions = {},
    ) {
        this.supported = Boolean(this._ctor);
    }

    /** Committed value plus the in-flight fragment — what the input shows. */
    get displayValue(): string {
        const value = this._host.getValue();
        return this.listening && this._interim ? join(value, this._interim) : value;
    }

    /** True while `displayValue` differs from the committed draft. */
    get hasInterim(): boolean {
        return this.listening && this._interim !== '';
    }

    clearError(): void {
        this.error = null;
        this._host.onChange();
    }

    start(): void {
        if (!this._ctor || this.listening) {
            return;
        }
        // Deferred to the first real listen, not the constructor: the host is
        // mounted at app root, so every page load used to fire this probe —
        // `SpeechRecognition.available()` is exactly the kind of experimental,
        // on-device-model check that has no business running on pages nobody
        // dictated on.
        if (!this._localProcessingProbed) {
            this._localProcessingProbed = true;
            void this.probeLocalProcessing();
        }

        const recognition = new this._ctor();
        recognition.continuous = true;
        recognition.interimResults = true;
        // Deliberately NOT `navigator.language`: that is the browser's UI
        // locale, not the language being spoken. Measured on a maintainer's
        // machine, `<html lang>` is empty and `navigator.language` is `uk-UA`,
        // so the composer was handing English speech to a Ukrainian model.
        // PayHOA's content — resident names, addresses, ledger terms — is
        // English, so English is the right default; an explicit `<html lang>`
        // still wins if the app ever sets one.
        recognition.lang = this._recognitionLang();
        if (this._localProcessing) {
            recognition.processLocally = true;
        }

        recognition.onresult = (event) =>
            this._host.run(() => {
                // `results` accumulates across the session — only walk the
                // genuinely new tail, or earlier finals get committed twice.
                // Per spec the list never shrinks within a session; if it does
                // anyway the bookkeeping is for a list that no longer exists,
                // and walking from a count past the end would commit nothing
                // while the UI stayed live — the field would look frozen.
                const shrunk = event.results.length < this._consumedFinals;
                if (shrunk) {
                    this._consumedFinals = 0;
                }
                // Never step back over a result already committed, however far
                // back `resultIndex` points.
                const from = Math.max(event.resultIndex, this._consumedFinals);
                const finals: string[] = [];
                const inFlight: string[] = [];
                for (let i = from; i < event.results.length; i++) {
                    const result = event.results[i];
                    const text = result[0].transcript.trim();
                    if (result.isFinal) {
                        if (text) {
                            finals.push(text);
                        }
                        this._consumedFinals = i + 1;
                    } else if (text) {
                        inFlight.push(text);
                    }
                }
                // Chrome usually leads a follow-on transcript with a space, but
                // that is convention, not contract — seam the pieces ourselves.
                if (finals.length) {
                    this._commit(join(this._host.getValue(), punctuateChunk(finals.join(' '))));
                }
                this._interim = inFlight.join(' ');
                dictationDiag('onresult', {
                    resultIndex: event.resultIndex,
                    total: event.results.length,
                    from,
                    consumed: this._consumedFinals,
                    shrunk,
                    committed: finals.length,
                    valueLength: this._host.getValue().length,
                    interimLength: this._interim.length,
                    inAngularZone: this._host.isInAngularZone(),
                    // Lengths only — dictated speech routinely carries a
                    // resident's name, address or balance, and this trace is
                    // meant to be copied out of localStorage into a ticket.
                    results: Array.from({ length: event.results.length }, (_, i) => ({
                        final: event.results[i].isFinal,
                        textLength: event.results[i][0].transcript.length,
                    })),
                });
                this._host.onChange();
            });

        for (const name of TRACE_EVENTS) {
            recognition[name] = () =>
                dictationDiag(name, {
                    listening: this.listening,
                    interimLength: this._interim.length,
                    inAngularZone: this._host.isInAngularZone(),
                });
        }

        recognition.onerror = (event) =>
            this._host.run(() => {
                dictationDiag('onerror', {
                    error: event.error,
                    listening: this.listening,
                    inAngularZone: this._host.isInAngularZone(),
                });
                if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                    this.error = MIC_BLOCKED_MESSAGE;
                }
                // 'no-speech' and 'aborted' just end the session quietly
                this._settle(true);
                this._host.onChange();
            });

        // the browser ends recognition itself after sustained silence —
        // treat that as a clean stop, words kept
        recognition.onend = () =>
            this._host.run(() => {
                dictationDiag('onend', {
                    listening: this.listening,
                    interimLength: this._interim.length,
                    inAngularZone: this._host.isInAngularZone(),
                });
                if (this.listening) {
                    this._settle(true);
                    this._host.onChange();
                }
            });

        dictationDiag('start', { localProcessing: this._localProcessing, lang: recognition.lang });
        this._recognition = recognition;
        this.error = null;
        this._interim = '';
        this._consumedFinals = 0;
        this._preListenValue = this._host.getValue();
        this.listening = true;

        try {
            recognition.start();
        } catch {
            this._settle(false);
        }
        this._host.onChange();
    }

    /** Stops and keeps everything spoken so far, interim included. */
    stop(): void {
        if (!this.listening) {
            return;
        }
        this._settle(true);
        this._host.onChange();
    }

    /** Stops and discards everything spoken this session, interim and final. */
    cancel(): void {
        if (!this.listening) {
            return;
        }
        this._settle(false);
        this._commit(this._preListenValue);
        this._host.onChange();
    }

    toggle(): void {
        if (this.listening) {
            this._settle(true);
            this._host.onChange();
        } else {
            this.start();
        }
    }

    /**
     * Synchronous snapshot for send: stops listening and returns the full text.
     * `recognition.stop()` would finalize asynchronously, so anything relying
     * on that would drop the last half-sentence — this aborts instead.
     */
    capture(): string {
        const value = this._host.getValue();
        // What was written, not what was computed — `_commit` may cap it, and
        // the send must carry the same text the field shows.
        const text = this._interim ? this._commit(join(value, punctuateChunk(this._interim))) : value;
        this._interim = '';
        this._settle(false);
        this._host.onChange();
        return text;
    }

    /** Route the textarea's change value through this — typing takes over. */
    onInputChange(next: string): void {
        // the field displays committed + interim, so the typed value already
        // contains the interim fragment — adopt it wholesale and go quiet
        const wasListening = this.listening;
        if (wasListening) {
            this._interim = '';
            this._settle(false);
        }
        this._commit(next);
        // plain typing changes no dictation state, so it must not re-render the host per keystroke
        if (wasListening) {
            this._host.onChange();
        }
    }

    /** Kill the session without touching state — for the host's teardown. */
    destroy(): void {
        this._detach(this._recognition);
        this._recognition = null;
    }

    /**
     * The Web Speech spec gained on-device recognition (`processLocally`) after
     * the original API. Where the browser reports the language as locally
     * available we prefer it, because otherwise Chrome streams the captured
     * audio to Google's servers — and residents' names and balances get spoken
     * into this composer. Fire-and-forget from the first `start()`, not
     * construction — the host mounts at app root, so probing eagerly would run
     * this on every page load instead of only when someone dictates. `start()`
     * itself stays synchronous (it runs inside the click gesture), so it reads
     * the cached verdict and silently uses the cloud path if the probe from
     * that same call has not landed yet.
     */
    private async probeLocalProcessing(): Promise<void> {
        const available = this._ctor?.available;
        if (typeof available !== 'function') {
            return;
        }
        const lang = this._recognitionLang();
        try {
            const verdict = await available.call(this._ctor, { langs: [lang], processLocally: true });
            this._localProcessing = verdict === 'available';
        } catch {
            /* not supported after all — cloud path */
        }
    }

    /** One source for the recognition language; see the note in `start()`. */
    private _recognitionLang(): string {
        return (typeof document !== 'undefined' && document.documentElement.lang) || 'en-US';
    }

    /** Write to the draft, capped at `maxLength`; returns what was written. */
    private _commit(next: string): string {
        const max = this._options.maxLength;
        const written = max !== undefined && next.length > max ? next.slice(0, max) : next;
        this._host.setValue(written);
        return written;
    }

    /** Tear down recognition; optionally keep the in-flight fragment. */
    private _settle(keepInterim: boolean): void {
        dictationDiag('settle', { keepInterim, wasListening: this.listening });
        if (keepInterim && this._interim) {
            this._commit(join(this._host.getValue(), punctuateChunk(this._interim)));
        }
        this._interim = '';
        this.listening = false;

        const recognition = this._recognition;
        this._recognition = null;
        this._detach(recognition);
    }

    private _detach(recognition: SpeechRecognitionLike | null): void {
        if (!recognition) {
            return;
        }
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        for (const name of TRACE_EVENTS) {
            recognition[name] = null;
        }
        try {
            recognition.abort();
        } catch {
            /* already gone */
        }
    }
}
